import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Search } from "lucide-react";
import { paths } from "@shared-core/routing/routes";
import {
  SUPPLIER_PICK_TABS,
  type PickDespatchResult,
  type SupplierPickRow,
  type SupplierPickSort,
  type SupplierPickTab,
} from "@shared-core/data/supplier-picks-adapter";
import { useSupplierPicksAdapter } from "@shared-core/data/SupplierPortalAdapterContext";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { useFieldErrorWrite } from "@shared-core/domain/useFieldErrorWrite";
import { useLoad } from "@shared-core/domain/useLoad";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import {
  ACTION_WORDING,
  PICK_WORDING,
  STAFF_VIEW_WORDING,
  count,
  dayMonth,
  pickDeliverToSummary,
  pickTabCountTone,
  pickTabLabel,
  pickStatus,
  platformToday,
} from "@shared-core/domain/supplier-portal-wording";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import { Input } from "../../app/components/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../app/components/primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { cn } from "../../app/components/primitives/utils";
import { StatusPill } from "../../app/components/domain/StatusPill";
import { StaffViewReadOnlyNote } from "../../app/components/domain/StaffViewReadOnly";
import { CountBadge } from "../shell/SignedInShell";
import { useSupplierSession } from "../shell/session";
import {
  STAFF_VIEW_READ_ONLY_NOTE_ID,
  useIsStaffView,
} from "../shell/staffView";
import { EmptyState, LoadFailed, LoadingCard } from "../shell/states";
import {
  despatchEntry,
  emptyDespatchFields,
  foldDespatchErrors,
  unplacedDespatchErrors,
  type DespatchFields,
} from "./despatchFields";
import { PickDespatchFields } from "./PickDespatchFields";
import { PickResults } from "./PickResults";

const PAGE_SIZE = 25;
const DEFAULT_SORT: SupplierPickSort = "created_at";
const SORT_CHOICES: { value: SupplierPickSort; label: string }[] = [
  { value: "created_at", label: PICK_WORDING.sortOldest },
  { value: "required_on", label: PICK_WORDING.sortRequiredSoonest },
  { value: "-required_on", label: PICK_WORDING.sortRequiredLatest },
];

function tabFrom(value: string | null): SupplierPickTab {
  return SUPPLIER_PICK_TABS.find((t) => t === value) ?? "to_pick";
}

function sortFrom(value: string | null): SupplierPickSort {
  return SORT_CHOICES.find((s) => s.value === value)?.value ?? DEFAULT_SORT;
}

/** A pick that goes to several addresses is recorded from its own page, one address at a time. */
const isSelectable = (row: SupplierPickRow) =>
  row.state === "to_pick" && row.deliver_to.length === 1;

/**
 * `/picks` (login only; spec 14 §8.2): what is waiting to leave a warehouse, by tab (To pick / Despatched today /
 * All), searchable, oldest first. On To pick, ticking rows and choosing Record despatch records them together with
 * one reference, date and carrier; each pick is recorded on its own, so the result says which went through and why
 * any did not. Filtering, search, sort and paging are the server's (docs/list-endpoints.md); the page only renders.
 */
export function PicksPage() {
  const picks = useSupplierPicksAdapter();
  const sessionState = useSupplierSession();
  const isStaffView = useIsStaffView();
  const warehouses = sessionState?.session.warehouses ?? [];
  const [params, setParams] = useSearchParams();
  const warehouseId = params.get("warehouse") ?? warehouses[0]?.id ?? null;
  const warehouseName =
    warehouses.find((w) => w.id === warehouseId)?.name ?? null;
  const tab = tabFrom(params.get("tab"));
  const sort = sortFrom(params.get("sort"));
  const page = Math.max(1, Number(params.get("page")) || 1);
  const [query, setQuery] = useState(params.get("q") ?? "");
  const q = useDebouncedValue(query.trim(), 300);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [fields, setFields] = useState<DespatchFields>(emptyDespatchFields(""));
  const [outcome, setOutcome] = useState<{
    results: PickDespatchResult[];
    lineRefs: Record<string, string>;
  } | null>(null);
  const write = useFieldErrorWrite("We couldn't send this to Pollards.");

  // A new search starts again at page 1 (list-endpoints contract).
  useEffect(() => {
    if ((params.get("q") ?? "") === q) return;
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (q) next.set("q", q);
        else next.delete("q");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [q, params, setParams]);

  const listKey = `${warehouseId}|${tab}|${sort}|${q}|${page}`;
  const list = useLoad(
    () =>
      picks.listPicks({
        warehouseId,
        tab,
        sort,
        q: q || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    listKey,
  );

  // What was ticked belongs to the rows it was ticked on.
  const resetWrite = write.reset;
  useEffect(() => {
    setSelected(new Set());
    setIsFormOpen(false);
    resetWrite();
  }, [listKey, resetWrite]);

  const searchWith = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    }
    next.delete("page");
    return next;
  };
  const tabHref = (next: SupplierPickTab) => {
    const search = searchWith({ tab: next === "to_pick" ? null : next });
    const text = search.toString();
    return text ? `?${text}` : paths.supplierPicks();
  };
  const pageHref = (n: number) => {
    const search = new URLSearchParams(params);
    if (n > 1) search.set("page", String(n));
    else search.delete("page");
    return `?${search.toString()}`;
  };

  const items = list.data?.items ?? [];
  const selectable = items.filter(isSelectable);
  const allSelected =
    selectable.length > 0 && selectable.every((p) => selected.has(p.pick_id));
  const toggle = (pickId: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (!next.delete(pickId)) next.add(pickId);
      return next;
    });

  const errors = foldDespatchErrors(write.fieldErrors);
  const submit = () =>
    void write.run(async () => {
      const chosen = items.filter((p) => selected.has(p.pick_id));
      const results = await picks.recordPickDespatches(
        chosen.map((p) => despatchEntry(p.pick_id, fields)),
      );
      setOutcome({
        results,
        lineRefs: Object.fromEntries(
          chosen.map((p) => [p.pick_id, p.line_ref]),
        ),
      });
      setSelected(new Set());
      setIsFormOpen(false);
      list.reload();
      sessionState?.refresh();
    });

  if (warehouses.length === 0) {
    return (
      <Card>
        <EmptyState title={PICK_WORDING.noWarehouses}>
          <p>{PICK_WORDING.noWarehousesWhy}</p>
        </EmptyState>
      </Card>
    );
  }

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-semibold">Picks</h1>
        {warehouses.length > 1 && warehouseId && (
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              {PICK_WORDING.warehouse}
            </span>
            <Select
              value={warehouseId}
              onValueChange={(next) =>
                setParams(searchWith({ warehouse: next }))
              }
            >
              <SelectTrigger
                size="sm"
                className="w-40"
                aria-label={PICK_WORDING.warehouse}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {warehouses.map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-x-1 gap-y-3">
        <nav aria-label="Pick status" className="flex gap-1 overflow-x-auto">
          {SUPPLIER_PICK_TABS.map((t) => {
            const n = list.data?.tabCounts[t];
            return (
              <Link
                key={t}
                to={tabHref(t)}
                aria-current={t === tab ? "page" : undefined}
                className={cn(
                  "border-b-2 px-2.5 py-2 text-sm whitespace-nowrap",
                  t === tab
                    ? "border-primary font-semibold text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {pickTabLabel(t)}
                {n !== undefined && n > 0 && (
                  <CountBadge
                    n={n}
                    tone={pickTabCountTone(t)}
                    label={`${n} picks`}
                  />
                )}
              </Link>
            );
          })}
        </nav>
        <span className="flex-1" />
        <Select
          value={sort}
          onValueChange={(next) =>
            setParams(searchWith({ sort: next === DEFAULT_SORT ? null : next }))
          }
        >
          <SelectTrigger
            size="sm"
            className="w-44"
            aria-label={PICK_WORDING.sortLabel}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_CHOICES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="relative w-full sm:w-64">
          <span className="sr-only">{PICK_WORDING.searchPlaceholder}</span>
          <Search
            aria-hidden
            className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-muted-foreground"
          />
          <Input
            type="search"
            className="pl-8"
            placeholder={PICK_WORDING.searchPlaceholder}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
      </div>

      {outcome && (
        <PickResults
          results={outcome.results}
          lineRefs={outcome.lineRefs}
          onDismiss={() => setOutcome(null)}
        />
      )}

      {selected.size > 0 && (
        <Card className="gap-3 p-3">
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm font-semibold" role="status">
              {PICK_WORDING.selectedCount(selected.size)}
            </p>
            <Button
              size="sm"
              aria-expanded={isFormOpen}
              onClick={() => {
                if (!isFormOpen && fields.date === "") {
                  setFields(emptyDespatchFields(platformToday()));
                }
                setIsFormOpen(!isFormOpen);
              }}
            >
              {`${ACTION_WORDING.recordDespatch} for ${selected.size}`}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelected(new Set())}
            >
              {ACTION_WORDING.cancel}
            </Button>
          </div>
          {isFormOpen && (
            <form
              noValidate
              aria-label={PICK_WORDING.bulkTitle}
              className="grid gap-3 rounded-lg border bg-card p-3"
              onSubmit={(event) => {
                event.preventDefault();
                submit();
              }}
            >
              <p className="text-sm font-semibold">{PICK_WORDING.bulkTitle}</p>
              <p className="text-xs text-muted-foreground">
                {PICK_WORDING.bulkHint}
              </p>
              {write.banner && (
                <p role="alert" className="text-sm text-red-600">
                  {write.banner}
                </p>
              )}
              {unplacedDespatchErrors(errors).map((message) => (
                <p key={message} className="text-sm text-red-600">
                  {message}
                </p>
              ))}
              <PickDespatchFields
                idPrefix="bulk"
                value={fields}
                onChange={setFields}
                errors={errors}
                today={platformToday()}
              />
              <div className="flex justify-end">
                <Button type="submit" disabled={write.isBusy}>
                  {ACTION_WORDING.recordDespatch}
                </Button>
              </div>
            </form>
          )}
        </Card>
      )}

      {list.data ? (
        items.length === 0 ? (
          <Card>
            <EmptyState
              title={
                q
                  ? PICK_WORDING.noMatch(q)
                  : PICK_WORDING.empty(tab, warehouseName)
              }
            />
          </Card>
        ) : (
          <>
            {isStaffView && tab === "to_pick" && (
              <StaffViewReadOnlyNote>
                {STAFF_VIEW_WORDING.picksDespatch}
              </StaffViewReadOnlyNote>
            )}
            <PicksTable
              items={items}
              withSelection={tab === "to_pick"}
              selected={selected}
              allSelected={allSelected}
              onToggle={toggle}
              onToggleAll={() =>
                setSelected(
                  allSelected
                    ? new Set()
                    : new Set(selectable.map((p) => p.pick_id)),
                )
              }
            />
            {list.data.totalPages > 1 && (
              <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
                Page {list.data.page} of {list.data.totalPages}
                {page > 1 && (
                  <Button asChild variant="outline" size="sm">
                    <Link to={pageHref(page - 1)}>Previous</Link>
                  </Button>
                )}
                {page < list.data.totalPages && (
                  <Button asChild variant="outline" size="sm">
                    <Link to={pageHref(page + 1)}>Next</Link>
                  </Button>
                )}
              </div>
            )}
          </>
        )
      ) : list.error ? (
        <LoadFailed
          what="your picks"
          message={mutationErrorMessage(
            list.error,
            "Something went wrong reaching Pollards.",
          )}
          onRetry={list.reload}
        />
      ) : (
        <LoadingCard what="your picks" />
      )}
    </div>
  );
}

function PicksTable({
  items,
  withSelection,
  selected,
  allSelected,
  onToggle,
  onToggleAll,
}: {
  items: SupplierPickRow[];
  withSelection: boolean;
  selected: Set<string>;
  allSelected: boolean;
  onToggle: (pickId: string) => void;
  onToggleAll: () => void;
}) {
  // A staff view draws the ticks switched off, so the bulk Record despatch bar never opens.
  const isStaffView = useIsStaffView();
  return (
    <Card className="gap-0 overflow-x-auto py-0">
      <Table>
        <TableHeader>
          <TableRow>
            {withSelection && (
              <TableHead className="w-8">
                <input
                  type="checkbox"
                  className="size-4 accent-primary"
                  aria-label="Select all picks on this page"
                  disabled={isStaffView}
                  aria-describedby={
                    isStaffView ? STAFF_VIEW_READ_ONLY_NOTE_ID : undefined
                  }
                  checked={allSelected}
                  onChange={onToggleAll}
                />
              </TableHead>
            )}
            <TableHead>Our reference</TableHead>
            <TableHead>Customer reference</TableHead>
            <TableHead>Item</TableHead>
            <TableHead className="text-right">Quantity</TableHead>
            <TableHead>Deliver to</TableHead>
            <TableHead>Required by</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>
              <span className="sr-only">Open</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((row) => {
            const status = pickStatus(row);
            return (
              <TableRow key={row.pick_id}>
                {withSelection && (
                  <TableCell>
                    <input
                      type="checkbox"
                      className="size-4 accent-primary"
                      aria-label={PICK_WORDING.selectRow(row.line_ref)}
                      checked={selected.has(row.pick_id)}
                      disabled={isStaffView || !isSelectable(row)}
                      aria-describedby={
                        isStaffView ? STAFF_VIEW_READ_ONLY_NOTE_ID : undefined
                      }
                      title={
                        row.deliver_to.length > 1
                          ? PICK_WORDING.needsAddress
                          : undefined
                      }
                      onChange={() => onToggle(row.pick_id)}
                    />
                  </TableCell>
                )}
                <TableCell className="font-mono text-xs">
                  {row.line_ref}
                </TableCell>
                <TableCell>{row.customer_reference ?? "—"}</TableCell>
                <TableCell className="whitespace-normal">
                  <span className="font-mono text-xs">{row.stock_code}</span>
                  <span className="block text-muted-foreground">
                    {row.description}
                  </span>
                </TableCell>
                <TableCell className="text-right whitespace-normal">
                  {count(row.quantity)}
                  {row.stock_parts_label && (
                    <span className="block text-xs text-muted-foreground">
                      {row.stock_parts_label}
                    </span>
                  )}
                </TableCell>
                <TableCell className="whitespace-normal">
                  {pickDeliverToSummary(row.deliver_to)}
                </TableCell>
                <TableCell>
                  {row.required_on ? dayMonth(row.required_on) : "—"}
                </TableCell>
                <TableCell>
                  <StatusPill label={status.label} tone={status.tone} />
                </TableCell>
                <TableCell className="text-right">
                  <Button asChild variant="outline" size="sm">
                    <Link
                      to={paths.supplierPick({ pickId: row.pick_id })}
                      aria-label={`${ACTION_WORDING.open} ${row.line_ref}`}
                    >
                      {ACTION_WORDING.open}
                    </Link>
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}
